<template>
  <list-layout
    no-counts
    no-search
    no-apply-btn
  >
    <template v-slot:beforeFilters>
      <b-row>
        <b-col>
          <filter-builder
            ref="statementsFilters"
            :id="userPreferenceNames.PAYOUT_STATEMENTS_FILTER"
            :filters="filters"
            :apply-filters="applyFilters"
            :reset-filters="resetFilters"
            :filters-pending="filtersPending"
            :filters-applied="filtersApplied"
            @filter="onFilter"
          />
        </b-col>
      </b-row>
    </template>

    <template v-slot:table>
      <t-table
        :columns="columns"
        :options="options"
        :limit="limit"
        ref="statementsList"
        id="payout-statements-table"
        @loaded="onLoaded"
        @sorted="onSorted"
        @pagination="onPagination"
        @loading="onLoading"
      >
        <template v-slot:merge>
          <b-button
            v-if="isInReviewOrReportGenerated && permissions.pages[Pages.PayoutsList].canWrite"
            class="btn-outline-red"
            @click="$bvModal.show('generate-statements-modal-tab')"
          >
            {{ $t("generate_statements") | capitalize }}
          </b-button>
        </template>
        <template v-slot:counts>
          <div class="d-flex">
            <pagination
              class="smaller p-0"
              :page="page"
              :count="count"
              :loading="loading"
              v-model="limit"
              @limit="onLimit"
            />
          </div>
        </template>

        <!-- Type -->
        <template v-slot:type="{ row }">
          {{ (row.type === statementTypes.RIGHTS_HOLDER ? $t("rights_holder") : $t("payee")) | capitalize }}
        </template>

        <!-- Export date -->
        <template v-slot:exportDate="{ row }">
          {{ row.exportDate ? parseDate(row.exportDate, $config.DATETIME_FORMAT) : "-" }}
        </template>

        <!-- Export status -->
        <template v-slot:exportStatus="{ row }">
          <status
            v-if="row.exportStatus === statementExportStatuses.COMPLETED"
            name="completed"
            :text="capitalize($t('completed'))"
            :icon="['fas', 'check-circle']"
            color="green"
          />
          <status
            v-else-if="row.exportStatus === statementExportStatuses.ERROR"
            name="error"
            :text="capitalize($t('error'))"
            :icon="['fas', 'exclamation-circle']"
            color="red"
          />
          <status
            v-else
            :name="row.exportStatus === statementExportStatuses.IN_PROGRESS ? 'in_progress' : 'pending'"
            :text="capitalize($t(row.exportStatus === statementExportStatuses.IN_PROGRESS ? 'in_progress' : 'pending'))"
            :icon="['fas', 'question-circle']"
            color="orange"
          />
        </template>

        <!-- Societies -->
        <template v-slot:societies="{ row }">
          <span v-if="!row.societies || !row.societies.length">-</span>
          <span v-else-if="row.societies.length === 1">{{ row.societies[0] }}</span>
          <span
            v-else
            v-b-popover.hover.top="row.societies.join(', ')"
            class="blue clickable"
            tabindex="0"
          >
            {{ row.societies[0] }} +{{ row.societies.length - 1 }}
          </span>
        </template>

        <!-- Status -->
        <template v-slot:status="{ row }">
          <status
            v-if="row.status === statementStatuses.FINALIZED"
            name="finalized"
            :text="capitalize($t('finalized'))"
            :icon="['fas', 'check-circle']"
            color="green"
          />
          <status
            v-else-if="row.status === statementStatuses.PREVIEW"
            name="preview"
            :text="capitalize($t('preview'))"
            :icon="['fas', 'eye']"
            color="orange"
          />
          <span v-else>-</span>
        </template>

        <!-- Action -->
        <template v-slot:action="{ row }">
          <b-button
            v-if="row.exportStatus === statementExportStatuses.COMPLETED"
            size="sm"
            class="btn-outline-red"
            :disabled="downloadingId === row.id"
            :id="row.id + '-download-btn'"
            @click="downloadStatement(row)"
          >
            <b-spinner v-if="downloadingId === row.id" small />
            {{ $t("export") | capitalize }}
          </b-button>
        </template>
      </t-table>
    </template>

    <modal
      id="generate-statements-modal-tab"
      :cancel-title="$t('cancel') | capitalize"
      :ok-title="$t('confirm') | capitalize"
      @ok="generateStatements()"
      @cancel="$bvModal.hide('generate-statements-modal-tab')"
      modal-type="info"
      centered
      :custom-title="$t('generate_statements') | capitalize"
    >
      {{ $t("confirm_generate_statements") }}
    </modal>
  </list-layout>
</template>

<script>
import { capitalize, get, snakeCase } from "lodash"
import {
  filterTypes,
  payoutStatuses,
  perPageItemsLg,
  statementExportStatuses,
  statementStatuses,
  statementTypes,
  userPreferenceNames,
} from "@/constants"
import { mapMutations, mapState } from "vuex"
import FilterBuilder from "@/components/FilterBuilder"
import { Pages } from "@/utils/pages"
import Pagination from "@/components/Pagination"
import { listRouteMixin } from "@/utils/mixins"
import parseDate from "@/utils/date-parser"

export default {
  name: "StatementsSection",
  mixins: [listRouteMixin],
  components: {
    FilterBuilder,
    Pagination,
  },
  props: {
    isSubTab: {
      type: Boolean,
      default: false,
    },
    payoutData: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      page: 1,
      count: 0,
      limit: perPageItemsLg,
      loading: false,
      downloadingId: null,
      capitalize,
      statementTypes,
      statementExportStatuses,
      statementStatuses,
      Pages,
      userPreferenceNames,
      filters: {
        statementId: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("statement_id")),
        },
        type: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT,
          label: capitalize(this.$t("statement_type")),
          options: [
            { value: statementTypes.PAYEE, text: capitalize(this.$t("payee")) },
            { value: statementTypes.RIGHTS_HOLDER, text: capitalize(this.$t("rights_holder")) },
          ],
        },
        relatedObjectName: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("name")),
        },
        societies: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT,
          label: capitalize(this.$t("societies")),
          emptyLabel: capitalize(this.$t("all")),
          options: [],
        },
        exportDate: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("date")),
          noWatch: true,
        },
        status: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT,
          label: capitalize(this.$t("status")),
          options: [
            { value: statementStatuses.PREVIEW, text: capitalize(this.$t("preview")) },
            { value: statementStatuses.FINALIZED, text: capitalize(this.$t("finalized")) },
          ],
        },
        exportStatus: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT,
          label: capitalize(this.$t("export_status")),
          options: [
            { value: statementExportStatuses.PENDING, text: capitalize(this.$t("pending")) },
            { value: statementExportStatuses.IN_PROGRESS, text: capitalize(this.$t("in_progress")) },
            { value: statementExportStatuses.COMPLETED, text: capitalize(this.$t("completed")) },
            { value: statementExportStatuses.ERROR, text: capitalize(this.$t("error")) },
          ],
        },
      },
    }
  },
  computed: {
    ...mapState("user", ["permissions"]),
    ...mapState("consts", ["memberCollectives"]),
    isInReviewOrReportGenerated () {
      return [payoutStatuses.IN_REVIEW, payoutStatuses.REPORT_GENERATED].includes(this.payoutData.status)
    },
    options () {
      const self = this
      return {
        sortable: [
          "statementId",
          "relatedObjectName",
          "exportDate",
          "exportStatus",
          "status",
        ],
        headings: {
          statementId: this.$t("statement_id"),
          relatedObjectName: capitalize(this.$t("name")),
          type: capitalize(this.$t("statement_type")),
          exportDate: capitalize(this.$t("date")),
          exportStatus: capitalize(this.$t("export_status")),
          status: capitalize(this.$t("status")),
          societies: capitalize(this.$t("societies")),
          action: "",
        },
        width: {
          statementId: 100,
          relatedObjectName: 250,
          type: 130,
          exportDate: 180,
          exportStatus: 150,
          status: 130,
          societies: 150,
          action: 130,
        },
        responseAdapter ({ data }) {
          return {
            data: data.results,
            count: data.count,
          }
        },
        requestFunction () {
          const routeQuery = { ...self.$route.query }
          self.page = Number(get(routeQuery, "page", 1))

          const queryParams = {}
          for (const key of ["page", "limit", "sorting", "search"]) {
            if (routeQuery[key] !== undefined) queryParams[key] = routeQuery[key]
          }

          queryParams.payout_id__eq = self.payoutData?.id

          const exactFilters = [
            "statement_id",
            "type",
            "related_object_name",
            "status",
            "export_status",
          ]
          for (const key of exactFilters) {
            if (routeQuery[key]) queryParams[`${key}__eq`] = routeQuery[key]
          }

          // Societies are matched case-insensitively, so the stored casing
          // doesn't have to match the collective name shown in the dropdown
          if (routeQuery.societies) queryParams.societies__iexact = routeQuery.societies

          // Date range lands in the query as two values, parsed as __gte/__lte by the API
          if (routeQuery.export_date) queryParams.export_date = routeQuery.export_date

          if (!queryParams.sorting) {
            queryParams.sorting = "-export_date"
          } else {
            const toSortParam = (item) =>
              item.startsWith("-") ? `-${snakeCase(item)}` : snakeCase(item)
            queryParams.sorting = Array.isArray(queryParams.sorting)
              ? queryParams.sorting.map(toSortParam)
              : toSortParam(queryParams.sorting)
          }

          return self.$api.distributions.statementsList(queryParams)
        },
      }
    },
    columns () {
      return [
        "statementId",
        "type",
        "relatedObjectName",
        "societies",
        "exportDate",
        "exportStatus",
        "status",
        "action",
      ]
    },
  },
  methods: {
    ...mapMutations("alert", ["success", "error"]),
    parseDate,
    setSocietyOptions (collectives) {
      this.filters.societies.options = collectives.map(x => ({ text: x.name, value: x.name }))
    },
    onFilter (name, value) {
      if (name === "exportDate") {
        const range = value
          ? [
            this.parseDate(value[0], this.$config.ISO_DATE_FORMAT),
            this.parseDate(value[1], this.$config.ISO_DATE_FORMAT),
          ] : []
        this.changeFilter(name, range.some(date => !!date) ? range : null)
      }
    },
    getData () {
      this.$refs.statementsList.getData()
    },
    async generateStatements () {
      this.$emit("payout-status-update", payoutStatuses.REPORT_REQUESTED)
      try {
        await this.$api.payouts.generateStatements(this.payoutData.id)
        this.success(capitalize(this.$t("statements_generation_started")))
      } catch (err) {
        if (err.response?.status === 409) {
          this.error(capitalize(this.$t("payout_invalid_status_for_statements")))
        } else {
          this.error(`${capitalize(this.$t("error_occurred"))} \n ${JSON.stringify(err.response?.data)}`)
        }
      }
    },
    onLoaded ({ data }) {
      this.count = data.count
      this.loading = false
    },
    async downloadStatement (row) {
      this.downloadingId = row.id
      try {
        const response = await this.$api.distributions.getStatementDownloadUrl(row.id)
        const url = response.data
        if (url) {
          window.location.href = url
        } else {
          this.error(capitalize(this.$t("statement_not_available")))
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          this.error(capitalize(this.$t("statement_not_available")))
        } else {
          this.error(capitalize(this.$t("error_occurred")))
        }
      } finally {
        this.downloadingId = null
      }
    },
  },
  watch: {
    memberCollectives (value) {
      this.setSocietyOptions(value)
    },
  },
  mounted () {
    this.setSocietyOptions(this.memberCollectives)
    this.updateTableSortIcons(this.$refs.statementsList.$children[0], true)
  },
}
</script>
